import { DASHBOARD_TILES } from "@pentacode/core";
import { css, html, LitElement } from "lit";
import { customElement, state } from "lit/decorators.js";
import "./dashboard-tile";
import { Task } from "@lit/task";
import { shared } from "../../styles";
import "../rich-text-editor";
import { app } from "../../init";
import { debounce } from "../../lib/util";

@customElement("ptc-dashboard-tile-notes")
export class DashboardTileNotes extends LitElement {
    @state()
    private _saveTimeout?: number;

    @state()
    private _isSaving = false;

    @state()
    private _lastSavedContent?: string;

    private _notesTask = new Task(this, {
        task: async () => {
            const note = await app.api.getNote("textNote");
            if (note) {
                this._lastSavedContent = note.content;
            }
            return note;
        },
        args: () => [],
    });

    private _debouncedSave = debounce(async (content: string) => {
        this._isSaving = true;
        try {
            await app.api.saveNote({ content, type: "textNote" });
            this._lastSavedContent = content;
        } catch (error) {
            console.error("Failed to save note:", error);
        } finally {
            this._isSaving = false;
        }
    }, 2000);

    disconnectedCallback() {
        super.disconnectedCallback();
        if (this._saveTimeout) {
            clearTimeout(this._saveTimeout);
        }
    }

    private _handleContentChange(event: CustomEvent) {
        const content = event.detail.content;

        // Don't save if content hasn't changed
        if (content === this._lastSavedContent) {
            return;
        }
        this._debouncedSave(content);
    }

    static styles = [
        shared,
        css`
            :host {
                display: contents;
                position: relative;
            }

            ptc-dashboard-tile {
                --tile-content-padding: 0;
            }

            ptc-rich-text-editor {
                border: solid 1px transparent;
                border-top-left-radius: 0;
                border-top-right-radius: 0;
                border-bottom-left-radius: var(--border-radius);
                border-bottom-right-radius: var(--border-radius);
                height: 100%;
                overflow: hidden;
            }

            ptc-rich-text-editor:focus {
                border-color: var(--color-primary);
            }

            .save-indicator {
                display: flex;
                align-items: center;
                font-size: 1rem;
                gap: 0.25rem;
                color: var(--color-primary);
            }

            [part="spinner"] {
                font-size: 0.75rem;
            }

            [part="spinner"],
            [part="icon"] {
                width: 1lh;
                height: 1lh;
                line-height: inherit;

                transition: opacity 0.2s ease-in-out;

                @starting-style {
                    opacity: 0;
                }
            }
        `,
    ];

    render() {
        return html`
            <ptc-dashboard-tile
                style="--tile-icon-color: var(--yellow)"
                label=${DASHBOARD_TILES.notes}
                icon="pen-to-square"
                .size=${{ inline: 2, block: 2 }}
            >
                ${this._isSaving
                    ? html`<span slot="header" class="save-indicator">
                          <i part="icon" class="fa-solid fa-floppy-disk"></i>
                          <ptc-spinner part="spinner" active size="small"></ptc-spinner>
                      </span>`
                    : ""}
                ${this._notesTask.render({
                    initial: () => html``,
                    pending: () => html`
                        <ptc-spinner
                            class="place-self-center double-padded"
                            active
                            style="font-size: 1rem"
                        ></ptc-spinner>
                    `,
                    error: () =>
                        html`<div class="double-padded text-centering">Notizen konnten nicht geladen werden!</div>`,

                    complete: (note) => {
                        return html`<ptc-rich-text-editor
                            .content=${note?.content || "<p>Platz für deine Gedanken 💭</p>"}
                            @content-changed=${this._handleContentChange}
                        ></ptc-rich-text-editor>`;
                    },
                })}
            </ptc-dashboard-tile>
        `;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        "ptc-dashboard-tile-notes": DashboardTileNotes;
    }
}
